/**
 * AITransactionContext
 *
 * Global context for tracking AI model transactions across the application.
 * When an AI model is called (e.g., Perplexity, Claude, OpenAI) and returns a response,
 * the transaction is recorded here for display and posted to the analytics store.
 *
 * The WALLET DEBIT is not made here (security remediation slice 2, 2026-09-15):
 * every proxied model call is billed by the LLM proxy's server meter
 * (X-Prest0n-Meter: server → recordAccountExpense, see src/services/proxyMeter.ts),
 * and users/{uid}/wallet is server-write-only in firestore.rules. Until this
 * slice the browser wrote its own expense row and balance from here — the same
 * access that let a signed-in user set any balance and cash it out.
 *
 * This context integrates with:
 * - GlobalChatSystem (for chat-based AI interactions)
 * - JusticIsUrRight page (for legal research AI calls)
 * - Wallet page (for displaying transaction history)
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { useAuth } from './AuthContext';
import { useMaybeOLVRate } from './OLVRateContext';
import type { TokenUsageMetadata, AIProvider } from '../components/GlobalChatSystem/types';
import { calculateCost } from '../config/aiModelPricing';
import { shouldTagAsTest, USER_COST_MULTIPLIER } from '../config/agenticWorkflowConfig';
import { buildModelDisplay } from '../services/globalTools/modelDisplay';
import { getProviderCodeName } from '../config/providerCodeNames';

function getRecordUsageEventUrl(): string {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/recordUsageEvent`;
}

// ============================================================================
// Types
// ============================================================================

/**
 * Represents a single AI transaction for display and storage
 */
export interface AITransaction {
  /** Unique identifier */
  id: string;
  /** Timestamp in epoch milliseconds */
  timestamp: number;
  /** The raw model ID (e.g., "sonar-deep-research", "claude-opus-4-7") */
  modelId?: string;
  /** The AI model/tool display name (legacy, for backwards compatibility) */
  modelTool: string;
  /** The AI provider (perplexity, claude, openai) */
  provider: AIProvider;
  /** Number of input tokens */
  inputTokens: number;
  /** Number of output tokens */
  outputTokens: number;
  /** Number of reasoning tokens (if applicable) */
  reasoningTokens?: number;
  /** Number of search queries (for Perplexity) */
  searchQueries?: number;
  /** Fixed fee in USD (for flat-rate tools) */
  fixedFeeUsd: number;
  /** OLV tokens used/cost */
  olvTokens: number;
  /** USD equivalent cost */
  usdEquivalent: number;
  /** Source page/feature that triggered this transaction */
  source: string;
  /** Brief summary of what was completed (max 25 chars) */
  summary?: string;
  /** Response time in milliseconds */
  responseTimeMs?: number;
  /** Whether the transaction has been synced to Firestore */
  synced: boolean;
  /** Member ID of the user who triggered the transaction */
  memberId?: string;
  /** Transaction status for display purposes */
  status?: 'successful' | 'failed' | 'paused';
  /** Tags for categorization */
  tags?: string[];
}

/**
 * Input for recording a new AI transaction
 */
export interface RecordTransactionInput {
  /** Token usage metadata from the AI response */
  tokenUsage: TokenUsageMetadata;
  /** Source page/feature (e.g., "justiceisurright", "global_chat") */
  source: string;
  /** Optional override for model name */
  modelOverride?: string;
  /** Brief summary of what was completed (max 25 chars) */
  summary?: string;
}

/**
 * Context state
 */
interface AITransactionState {
  /** List of transactions (most recent first) */
  transactions: AITransaction[];
  /** Whether transactions are being loaded */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Total count of transactions for the current user */
  totalCount: number;
  /** Version counter for real-time updates (increments on each change) */
  version: number;
  /** The most recently added transaction (for real-time balance updates) */
  latestTransaction: AITransaction | null;
}

/**
 * Context value
 */
interface AITransactionContextValue {
  state: AITransactionState;
  /** Record a new AI transaction */
  recordTransaction: (input: RecordTransactionInput) => Promise<AITransaction | null>;
  /** Get transactions for display (with optional pagination) */
  getTransactions: (limit?: number, offset?: number) => AITransaction[];
  /** Clear all local transactions (doesn't affect Firestore) */
  clearLocalTransactions: () => void;
  /** Force refresh from localStorage */
  refreshFromStorage: () => void;
  /** Current OLV rate (USD per OLV token) */
  olvRateUsd: number;
  /** Convert USD to OLV tokens using current rate */
  usdToOlv: (usd: number) => number;
  /** The most recently added transaction (for real-time balance updates) */
  latestTransaction: AITransaction | null;
  /** Version counter that increments on every state change (for reliable change detection) */
  transactionVersion: number;
}

// ============================================================================
// Analytics usage event (server-authed, analytics-only — never the wallet)
// ============================================================================

async function postUsageEvent(params: {
  userId: string;
  source: string;
  tokenUsage: TokenUsageMetadata;
  usdCost: number;
}): Promise<void> {
  const { userId, source, tokenUsage, usdCost } = params;
  try {
    const authUser = getAuth().currentUser;
    if (!authUser) return;
    const idToken = await authUser.getIdToken();
    await fetch(getRecordUsageEventUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({
        tool_name: source,
        model_used: tokenUsage.model || 'unknown',
        input_tokens: tokenUsage.inputTokens || 0,
        output_tokens: tokenUsage.outputTokens || 0,
        ...(tokenUsage.reasoningTokens ? { reasoning_tokens: tokenUsage.reasoningTokens } : {}),
        // usdCost (calculateCost().totalCost) is ALREADY the 5X user
        // price — the previous `usdCost * 5` stored 25X in
        // usage_events, inflating the Wallet analytics dashboard 5x
        // vs the expense ledger for the same calls. our_cost_usd is
        // the true base (pre-markup) figure.
        our_cost_usd: usdCost / USER_COST_MULTIPLIER,
        user_cost_usd: usdCost,
        // Schema discriminator (2026-07-06): v1 docs (no field)
        // stored user_cost_usd at 25X of base and our_cost_usd at
        // 5X; v2 stores user_cost_usd = 5X and our_cost_usd = base.
        // Lets analytics/backfills tell the eras apart.
        cost_schema: 2,
        session_id: `${userId}_${new Date().toISOString().slice(0, 10)}`,
      }),
    });
  } catch (err) {
    console.warn('[AITransactionContext] recordUsageEvent failed (non-critical):', err);
  }
}

// ============================================================================
// Constants
// ============================================================================

/** OLV token price in USD (for conversion) */
const OLV_TOKEN_PRICE_USD = 0.001; // $0.001 per OLV token

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generate a unique 12-digit numerical transaction ID
 */
function generateTransactionId(): string {
  // Generate a 12-digit numerical code
  // Use timestamp last 6 digits + 6 random digits for uniqueness
  const timestampPart = (Date.now() % 1000000).toString().padStart(6, '0');
  const randomPart = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
  return timestampPart + randomPart;
}

/**
 * Fallback display when a transaction has no model id: the provider's
 * white-label family name ('Apex', 'Echo', 'Prism', 'Titan', …) or the
 * neutral 'AI Model'. The per-model display itself is delegated to the
 * consolidated buildModelDisplay (2026-07-06 — the local ladder here had
 * drifted: retired 'Nova'/'Swift' names, unversioned Opus defaulting to
 * Apex-4.7 instead of 4.8, and prefixed ids passing through raw).
 */
function providerFamilyDisplay(provider: AIProvider): string {
  const codeName = getProviderCodeName(provider);
  return codeName === 'system' ? 'AI Model' : codeName;
}

// ============================================================================
// Storage
// ============================================================================

const STORAGE_KEY = 'prest0_ai_transactions';
const MAX_LOCAL_TRANSACTIONS = 500; // Keep last 500 transactions locally

function loadTransactionsFromStorage(): AITransaction[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (err) {
    console.warn('[AITransactionContext] Failed to load transactions from storage:', err);
  }
  return [];
}

function saveTransactionsToStorage(transactions: AITransaction[]): void {
  try {
    // Keep only the most recent transactions
    const toSave = transactions.slice(0, MAX_LOCAL_TRANSACTIONS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (err) {
    console.warn('[AITransactionContext] Failed to save transactions to storage:', err);
  }
}

// ============================================================================
// Reducer
// ============================================================================

type AITransactionAction =
  | { type: 'SET_TRANSACTIONS'; payload: AITransaction[] }
  | { type: 'ADD_TRANSACTION'; payload: AITransaction }
  | { type: 'UPDATE_TRANSACTION'; payload: { id: string; updates: Partial<AITransaction> } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_TRANSACTIONS' };

function aiTransactionReducer(state: AITransactionState, action: AITransactionAction): AITransactionState {
  switch (action.type) {
    case 'SET_TRANSACTIONS':
      return {
        ...state,
        transactions: action.payload,
        totalCount: action.payload.length,
        version: state.version + 1,
        // Don't set latestTransaction on bulk load - only on new additions
      };
    case 'ADD_TRANSACTION':
      const newTransactions = [action.payload, ...state.transactions];
      return {
        ...state,
        transactions: newTransactions,
        totalCount: state.totalCount + 1,
        version: state.version + 1,
        latestTransaction: action.payload, // Track the newly added transaction
      };
    case 'UPDATE_TRANSACTION':
      return {
        ...state,
        transactions: state.transactions.map(tx =>
          tx.id === action.payload.id ? { ...tx, ...action.payload.updates } : tx
        ),
        version: state.version + 1,
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'CLEAR_TRANSACTIONS':
      return { ...state, transactions: [], totalCount: 0, version: state.version + 1, latestTransaction: null };
    default:
      return state;
  }
}

const initialState: AITransactionState = {
  transactions: [],
  isLoading: false,
  error: null,
  totalCount: 0,
  version: 0,
  latestTransaction: null,
};

// ============================================================================
// Context
// ============================================================================

const AITransactionContext = createContext<AITransactionContextValue | null>(null);

export function AITransactionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const olvRateContext = useMaybeOLVRate();
  const [state, dispatch] = useReducer(aiTransactionReducer, initialState);

  // Get the effective OLV rate (live from Firestore or fallback)
  const getEffectiveOlvRate = useCallback((): number => {
    if (olvRateContext) {
      return olvRateContext.getEffectiveRate();
    }
    return OLV_TOKEN_PRICE_USD; // Fallback to hardcoded rate
  }, [olvRateContext]);

  // Convert USD to OLV tokens using the current rate (returns precise fractional value)
  const usdToOlv = useCallback((usd: number): number => {
    const rate = getEffectiveOlvRate();
    return rate > 0 ? usd / rate : 0;
  }, [getEffectiveOlvRate]);

  // Load transactions from localStorage on mount
  useEffect(() => {
    const stored = loadTransactionsFromStorage();
    if (stored.length > 0) {
      dispatch({ type: 'SET_TRANSACTIONS', payload: stored });
    }
  }, []);

  // Save transactions to localStorage whenever they change
  useEffect(() => {
    if (state.transactions.length > 0) {
      saveTransactionsToStorage(state.transactions);
    }
  }, [state.transactions]);

  // Note: the wallet ledger row and balance are written by the proxy's server
  // meter as the model call completes — nothing here syncs to the wallet.

  /**
   * Record a new AI transaction
   */
  const recordTransaction = useCallback(async (input: RecordTransactionInput): Promise<AITransaction | null> => {
    const { tokenUsage, source, modelOverride, summary } = input;

    if (!tokenUsage) {
      console.warn('[AITransactionContext] No token usage provided, skipping transaction recording');
      return null;
    }

    // Use the same cost calculation as the token info popup
    const costBreakdown = calculateCost(tokenUsage);
    const usdCost = costBreakdown?.totalCost || 0;
    // Use the live OLV rate from Firestore
    const olvTokens = usdToOlv(usdCost);

    // Truncate summary to 60 characters if provided
    // Add "(test example)" suffix when in testing mode
    const baseSummary = summary ? summary.slice(0, 46) : undefined;
    const truncatedSummary = baseSummary && shouldTagAsTest()
      ? `${baseSummary} (test example)`
      : baseSummary;

    // Debug: log what model is being recorded for the transaction
    console.log('[AITransaction] Creating transaction with modelId:', tokenUsage.model, 'summary:', truncatedSummary);

    const transaction: AITransaction = {
      id: generateTransactionId(),
      timestamp: tokenUsage.timestamp?.getTime() || Date.now(),
      modelId: tokenUsage.model || undefined,
      modelTool: modelOverride || buildModelDisplay(tokenUsage, '', providerFamilyDisplay(tokenUsage.provider || 'unknown')),
      provider: tokenUsage.provider || 'unknown',
      inputTokens: tokenUsage.inputTokens || 0,
      outputTokens: tokenUsage.outputTokens || 0,
      reasoningTokens: tokenUsage.reasoningTokens,
      searchQueries: tokenUsage.searchQueries,
      fixedFeeUsd: 0, // No fixed fee for token-based pricing
      olvTokens,
      usdEquivalent: usdCost,
      source,
      summary: truncatedSummary,
      responseTimeMs: tokenUsage.responseTimeMs,
      synced: false,
      memberId: user?.profileId || undefined,
      status: 'successful', // Transactions are recorded on successful completion
    };

    dispatch({ type: 'ADD_TRANSACTION', payload: transaction });

    // Save to localStorage IMMEDIATELY to prevent race conditions
    // (useEffect save happens after render, which may be too late if user navigates)
    const currentTransactions = loadTransactionsFromStorage();
    const updatedTransactions = [transaction, ...currentTransactions].slice(0, MAX_LOCAL_TRANSACTIONS);
    saveTransactionsToStorage(updatedTransactions);

    // Analytics usage event (best-effort, non-blocking). The wallet debit for
    // this call was made by the proxy's server meter; this row feeds the wallet
    // Usage Dashboard, which the server meter does not fan out for these lanes.
    const userId = user?.id;
    if (userId) {
      void postUsageEvent({ userId, source, tokenUsage, usdCost });
    }

    return transaction;
  }, [user?.id, user?.profileId, usdToOlv]);

  /**
   * Get transactions for display
   */
  const getTransactions = useCallback((limit?: number, offset?: number): AITransaction[] => {
    const start = offset || 0;
    const end = limit ? start + limit : undefined;
    return state.transactions.slice(start, end);
  }, [state.transactions]);

  /**
   * Clear all local transactions
   */
  const clearLocalTransactions = useCallback(() => {
    dispatch({ type: 'CLEAR_TRANSACTIONS' });
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  /**
   * Force refresh from localStorage
   */
  const refreshFromStorage = useCallback(() => {
    const stored = loadTransactionsFromStorage();
    dispatch({ type: 'SET_TRANSACTIONS', payload: stored });
  }, []);

  const contextValue: AITransactionContextValue = {
    state,
    recordTransaction,
    getTransactions,
    clearLocalTransactions,
    refreshFromStorage,
    olvRateUsd: getEffectiveOlvRate(),
    usdToOlv,
    latestTransaction: state.latestTransaction,
    transactionVersion: state.version,
  };

  return (
    <AITransactionContext.Provider value={contextValue}>
      {children}
    </AITransactionContext.Provider>
  );
}

/**
 * Hook to access the AI transaction context
 */
export function useAITransactions(): AITransactionContextValue {
  const context = useContext(AITransactionContext);
  if (!context) {
    throw new Error('useAITransactions must be used within an AITransactionProvider');
  }
  return context;
}

/**
 * Hook that safely provides AI transaction recording without throwing
 * Use this in components that may not be wrapped in AITransactionProvider
 */
export function useAITransactionRecorder(): {
  recordTransaction: (input: RecordTransactionInput) => Promise<AITransaction | null>;
  isAvailable: boolean;
} {
  const context = useContext(AITransactionContext);

  const noopRecord = useCallback(async () => {
    console.warn('[AITransactionContext] Transaction recording not available - provider not mounted');
    return null;
  }, []);

  return {
    recordTransaction: context?.recordTransaction || noopRecord,
    isAvailable: !!context,
  };
}

/**
 * Hook that safely returns the AI transaction context without throwing
 * Returns null if not within an AITransactionProvider
 * Use this for components that need to observe transactions (e.g., for real-time balance updates)
 */
export function useMaybeAITransactions(): AITransactionContextValue | null {
  return useContext(AITransactionContext);
}

export default AITransactionContext;
